import React, { useState, useEffect, useRef } from 'react';
import { useCart } from 'react-use-cart';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  RiCustomerService2Line, 
  RiSendPlane2Fill, 
  RiCloseLine, 
  RiRobot2Line 
} from 'react-icons/ri';
import { 
  MdEmail, 
  MdShoppingCart, 
  MdLocalShipping, 
  MdHelpOutline, 
  MdChevronRight 
} from 'react-icons/md';
import { FaCheckCircle, FaSpinner } from 'react-icons/fa';
import { firestore, firebase } from '../firebaseConfig';
import { formatPrice } from '../utils/priceFormatter';

export default function DigitalAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showTooltip, setShowTooltip] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [activeFlow, setActiveFlow] = useState(null); // 'welcome' | 'cart' | 'track' | 'faq'
  const chatEndRef = useRef(null);

  const { isEmpty, items, cartTotal, totalItems } = useCart();

  // Scroll to bottom when messages list updates
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen]);

  // Initial welcome message
  useEffect(() => {
    setMessages([
      {
        id: 'welcome-init',
        sender: 'assistant',
        text: 'Hello! I am your Veth Interiors Digital Assistant. 🌟 I am here to guide you through your buying journey, help you track shipments, or claim special offers. How can I help you today?',
        timestamp: new Date()
      }
    ]);

    // Show tooltip alert if cart is not empty (Cart recovery prompt)
    if (!isEmpty && totalItems > 0) {
      setUnreadCount(1);
    }

    // Hide initial floating tooltip after 6 seconds
    const timer = setTimeout(() => setShowTooltip(false), 6000);
    return () => clearTimeout(timer);
  }, []);

  // Update unread badge when cart becomes non-empty (and assistant is closed)
  useEffect(() => {
    if (!isEmpty && totalItems > 0 && !isOpen) {
      setUnreadCount(prev => prev || 1);
    }
  }, [isEmpty, totalItems, isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    setUnreadCount(0);
    setShowTooltip(false);
  };

  const addMessage = (sender, text, type = 'text', data = null) => {
    const newMessage = {
      id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      sender,
      text,
      timestamp: new Date(),
      type,
      data
    };
    setMessages(prev => [...prev, newMessage]);
  };

  // 1. Welcome / Newsletter Flow
  const triggerWelcomeFlow = () => {
    setActiveFlow('welcome');
    addMessage('user', '🎁 Get 10% Welcome Discount');
    addMessage(
      'assistant',
      'We would love to welcome you to the Veth Interiors family! Subscribe to our newsletter to receive design inspiration and an instant 10% discount code.',
      'welcome-form'
    );
  };

  const handleSubscribe = async (email) => {
    if (!email || !email.includes('@')) {
      addMessage('assistant', 'Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      const emailClean = email.toLowerCase().trim();
      const existing = await firestore.collection('subscribers')
        .where('email', '==', emailClean).get();

      if (existing.empty) {
        await firestore.collection('subscribers').add({
          email: emailClean,
          source: 'digital-assistant-welcome',
          subscribedAt: firebase.firestore.FieldValue.serverTimestamp()
        });
      }

      addMessage('assistant', `Thank you for subscribing! Your email "${emailClean}" has been registered. 🎉\n\nUse promo code **WELCOME10** at checkout to claim your 10% discount!`, 'success');
      setActiveFlow(null);
    } catch (err) {
      console.error(err);
      addMessage('assistant', 'Oops, something went wrong saving your subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // 2. Cart Recovery Flow
  const triggerCartRecoveryFlow = () => {
    setActiveFlow('cart');
    addMessage('user', '🛒 Complete Checkout (15% Off)');
    if (isEmpty || totalItems === 0) {
      addMessage('assistant', 'Your shopping cart is currently empty. Browse our premium collections and add items to your cart to get custom discounts!');
      setActiveFlow(null);
      return;
    }

    addMessage(
      'assistant',
      `I noticed you have ${totalItems} item(s) waiting in your cart. To help you finish your buying journey, here is an exclusive 15% discount code: **CART15**.`,
      'cart-recovery',
      { items, cartTotal, totalItems }
    );
  };

  // 3. Shipping / Order Tracking Flow
  const triggerTrackingFlow = () => {
    setActiveFlow('track');
    addMessage('user', '📦 Track My Order');
    addMessage(
      'assistant',
      'I can fetch real-time shipping updates for you. Please enter the email address used at checkout, or your payment Transaction Ref (e.g. flw_ref or tx_ref).',
      'tracking-form'
    );
  };

  const handleTrackOrder = async (queryInput) => {
    if (!queryInput || queryInput.trim().length < 3) {
      addMessage('assistant', 'Please enter a valid search term.');
      return;
    }

    setLoading(true);
    const searchVal = queryInput.toLowerCase().trim();
    try {
      let queryRef;
      if (searchVal.includes('@')) {
        queryRef = firestore.collection('veth_payments')
          .where('payeeEmail', '==', searchVal);
      } else {
        queryRef = firestore.collection('veth_payments')
          .where('transactionId', '==', queryInput.trim());
      }

      const snapshot = await queryRef.orderBy('createdAt', 'desc').limit(5).get();
      
      if (snapshot.empty) {
        // Backup check in BuyAttempts just in case
        const attemptSnapshot = await firestore.collection('BuyAttempts')
          .where('payeeEmail', '==', searchVal)
          .limit(3).get();

        if (!attemptSnapshot.empty) {
          addMessage(
            'assistant',
            'I found a purchase attempt for your email, but the payment confirmation is still processing. If you already paid, please check back in a few minutes or contact support.'
          );
        } else {
          addMessage(
            'assistant',
            `I couldn't find any orders matching "${queryInput}". Make sure you entered the correct email or transaction ID.`
          );
        }
        setLoading(false);
        return;
      }

      const orders = [];
      snapshot.forEach(doc => {
        orders.push({ id: doc.id, ...doc.data() });
      });

      addMessage(
        'assistant',
        `I found ${orders.length} order(s) associated with your search. Here are the tracking details:`,
        'tracking-results',
        { orders }
      );
      setActiveFlow(null);
    } catch (err) {
      console.error(err);
      addMessage('assistant', 'There was an error searching for your order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // 4. FAQ Flow
  const triggerFAQFlow = () => {
    setActiveFlow('faq');
    addMessage('user', '💬 Common FAQs');
    addMessage(
      'assistant',
      'Here are answers to some frequently asked questions about orders, shipping, and refunds:',
      'faq-list'
    );
  };

  // Basic NLP Keyword responses
  const handleCustomSubmit = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const userText = inputValue;
    addMessage('user', userText);
    setInputValue('');

    const textClean = userText.toLowerCase();

    // Context triggers
    if (activeFlow === 'welcome-form' || activeFlow === 'welcome') {
      handleSubscribe(userText);
      return;
    }
    if (activeFlow === 'tracking-form' || activeFlow === 'track') {
      handleTrackOrder(userText);
      return;
    }

    setLoading(true);
    setTimeout(() => {
      // Analyze keywords
      if (textClean.includes('track') || textClean.includes('order') || textClean.includes('shipping') || textClean.includes('status') || textClean.includes('where')) {
        addMessage('assistant', 'Let me help you track your order.');
        triggerTrackingFlow();
      } else if (textClean.includes('discount') || textClean.includes('promo') || textClean.includes('code') || textClean.includes('coupon') || textClean.includes('save') || textClean.includes('offer')) {
        addMessage('assistant', 'Here are our active discount codes. You can subscribe for a welcome discount, or complete your checkout for a recovery offer.');
        triggerWelcomeFlow();
      } else if (textClean.includes('hello') || textClean.includes('hi') || textClean.includes('hey') || textClean.includes('assistant')) {
        addMessage('assistant', 'Hi there! How can I help you today? Please feel free to type a question or select one of the options below:');
      } else if (textClean.includes('cart') || textClean.includes('checkout') || textClean.includes('buy') || textClean.includes('bag')) {
        triggerCartRecoveryFlow();
      } else {
        addMessage(
          'assistant',
          "I'm still learning, but I can guide you through the buying journey! Select one of these quick options or type a question containing keywords like 'track', 'discount', or 'cart':"
        );
      }
      setLoading(false);
    }, 600);
  };

  return (
    <>
      {/* Floating Action Button */}
      <div className="fixed bottom-24 right-5 z-[9999] flex flex-col items-end">
        <AnimatePresence>
          {showTooltip && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.9 }}
              className="mb-3 px-4 py-2 bg-gradient-to-r from-amber-600 to-yellow-500 text-white text-sm font-semibold rounded-lg shadow-xl border border-amber-400 max-w-xs text-center backdrop-blur-md relative"
            >
              <div className="absolute right-4 bottom-[-6px] w-3 h-3 bg-yellow-500 rotate-45 border-r border-b border-amber-400" />
              {(!isEmpty && totalItems > 0) 
                ? `⚠️ Don't leave your cart! Claim a 15% discount.`
                : `Need help tracking your order or claiming a discount?`
              }
            </motion.div>
          )}
        </AnimatePresence>

        <button
          onClick={handleToggle}
          className="relative bg-gradient-to-r from-amber-600 to-yellow-600 text-white rounded-full w-14 h-14 flex items-center justify-center shadow-2xl hover:scale-105 active:scale-95 transition-all duration-300 group border border-amber-400"
        >
          {isOpen ? (
            <RiCloseLine size={28} className="transform rotate-90 transition-transform duration-300" />
          ) : (
            <RiCustomerService2Line size={26} className="animate-bounce" />
          )}

          {unreadCount > 0 && !isOpen && (
            <span className="absolute -top-1 -right-1 flex h-5 w-5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-5 w-5 bg-red-600 text-[10px] font-bold text-white items-center justify-center shadow-md border border-white">
                {unreadCount}
              </span>
            </span>
          )}
        </button>
      </div>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-[170px] md:bottom-[100px] right-5 z-[9999] w-[92vw] sm:w-[380px] h-[520px] rounded-2xl flex flex-col overflow-hidden shadow-2xl backdrop-blur-md bg-white/95 dark:bg-zinc-950/95 border border-amber-500/20 text-left font-sans"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-700 to-amber-900 text-white p-4 flex items-center justify-between shadow-md">
              <div className="flex items-center gap-3">
                <div className="relative bg-amber-600/30 p-2 rounded-full border border-amber-500/30">
                  <RiRobot2Line size={24} className="text-amber-300" />
                  <span className="absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-amber-800" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm tracking-wide">Veth Digital Assistant</h3>
                  <span className="text-xs text-amber-200/80 font-medium">Online & Ready to Guide You</span>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white/80 hover:text-white hover:bg-white/10 p-1.5 rounded-full transition"
              >
                <RiCloseLine size={22} />
              </button>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-zinc-300 scrollbar-track-transparent">
              {messages.map((msg) => (
                <div key={msg.id} className="space-y-1">
                  {/* Sender Bubble */}
                  <div className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[85%] rounded-2xl p-3.5 text-sm shadow-sm transition-all leading-relaxed whitespace-pre-line ${
                        msg.sender === 'user'
                          ? 'bg-amber-600 text-white rounded-tr-none'
                          : 'bg-zinc-100 dark:bg-zinc-800/80 text-zinc-800 dark:text-zinc-100 rounded-tl-none border border-zinc-200/50 dark:border-zinc-700/50'
                      }`}
                    >
                      {msg.text}
                    </div>
                  </div>

                  {/* Render Special Sub-components based on message type */}
                  {msg.type === 'welcome-form' && (
                    <div className="pl-4 mt-2">
                      <SubscriptionForm onSubmit={handleSubscribe} loading={loading} />
                    </div>
                  )}

                  {msg.type === 'tracking-form' && (
                    <div className="pl-4 mt-2">
                      <TrackingForm onSubmit={handleTrackOrder} loading={loading} />
                    </div>
                  )}

                  {msg.type === 'cart-recovery' && msg.data && (
                    <div className="pl-4 mt-2">
                      <CartItemsPreview data={msg.data} onClose={handleToggle} />
                    </div>
                  )}

                  {msg.type === 'tracking-results' && msg.data && (
                    <div className="pl-4 mt-2">
                      <TrackingResults orders={msg.data.orders} />
                    </div>
                  )}

                  {msg.type === 'faq-list' && (
                    <div className="pl-4 mt-2">
                      <FAQList />
                    </div>
                  )}
                </div>
              ))}
              
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-zinc-100 dark:bg-zinc-800/80 rounded-2xl rounded-tl-none p-3 flex items-center gap-2 text-zinc-500 dark:text-zinc-400 text-xs border border-zinc-200/50 dark:border-zinc-700/50">
                    <FaSpinner className="animate-spin text-amber-600" />
                    Searching details...
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Quick Action Chips */}
            <div className="p-3 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/30 flex gap-2 overflow-x-auto whitespace-nowrap scrollbar-none">
              <button
                onClick={triggerWelcomeFlow}
                className="px-3 py-1.5 bg-white dark:bg-zinc-800 hover:bg-amber-50 dark:hover:bg-amber-950/20 border border-zinc-200 dark:border-zinc-700 hover:border-amber-500/30 rounded-full text-xs font-semibold text-zinc-700 dark:text-zinc-200 shadow-sm flex items-center gap-1.5 transition"
              >
                <MdEmail className="text-amber-600" /> 10% Welcome
              </button>

              {!isEmpty && totalItems > 0 && (
                <button
                  onClick={triggerCartRecoveryFlow}
                  className="px-3 py-1.5 bg-amber-50 dark:bg-amber-950/20 hover:bg-amber-100 dark:hover:bg-amber-950/40 border border-amber-200 dark:border-amber-900/50 rounded-full text-xs font-semibold text-amber-700 dark:text-amber-300 shadow-sm flex items-center gap-1.5 animate-pulse transition"
                >
                  <MdShoppingCart className="text-amber-600 animate-bounce" /> Checkout Code
                </button>
              )}

              <button
                onClick={triggerTrackingFlow}
                className="px-3 py-1.5 bg-white dark:bg-zinc-800 hover:bg-amber-50 dark:hover:bg-amber-950/20 border border-zinc-200 dark:border-zinc-700 hover:border-amber-500/30 rounded-full text-xs font-semibold text-zinc-700 dark:text-zinc-200 shadow-sm flex items-center gap-1.5 transition"
              >
                <MdLocalShipping className="text-amber-600" /> Track Shipment
              </button>

              <button
                onClick={triggerFAQFlow}
                className="px-3 py-1.5 bg-white dark:bg-zinc-800 hover:bg-amber-50 dark:hover:bg-amber-950/20 border border-zinc-200 dark:border-zinc-700 hover:border-amber-500/30 rounded-full text-xs font-semibold text-zinc-700 dark:text-zinc-200 shadow-sm flex items-center gap-1.5 transition"
              >
                <MdHelpOutline className="text-amber-600" /> FAQs
              </button>
            </div>

            {/* Custom Input Bar */}
            <form
              onSubmit={handleCustomSubmit}
              className="p-3 border-t border-zinc-100 dark:border-zinc-800 bg-white dark:bg-zinc-950 flex items-center gap-2"
            >
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Ask me something..."
                className="flex-1 py-2 px-3.5 border border-zinc-200 dark:border-zinc-800 rounded-xl bg-zinc-50 dark:bg-zinc-900/50 text-sm outline-none focus:border-amber-500 focus:bg-white dark:focus:bg-zinc-900 dark:text-white transition duration-200"
              />
              <button
                type="submit"
                disabled={!inputValue.trim()}
                className="bg-amber-600 disabled:opacity-40 hover:bg-amber-700 text-white p-2.5 rounded-xl shadow-md transition duration-200 flex items-center justify-center"
              >
                <RiSendPlane2Fill size={18} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// Subscription form sub-component
function SubscriptionForm({ onSubmit, loading }) {
  const [email, setEmail] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(email);
  };

  return (
    <form onSubmit={handleSubmit} className="w-[85%] bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-3.5 shadow-sm space-y-2">
      <p className="text-xs font-semibold text-zinc-500 dark:text-zinc-400">Newsletter Subscription</p>
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Enter your email"
        className="w-full text-xs p-2 border border-zinc-200 dark:border-zinc-800 rounded-lg outline-none focus:border-amber-500 bg-zinc-50 dark:bg-zinc-950 dark:text-white"
      />
      <button
        type="submit"
        disabled={loading}
        className="w-full py-2 bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-700 hover:to-yellow-700 text-white font-bold rounded-lg text-xs flex items-center justify-center gap-1"
      >
        {loading ? <FaSpinner className="animate-spin" /> : 'Subscribe Now'}
      </button>
    </form>
  );
}

// Order tracking form sub-component
function TrackingForm({ onSubmit, loading }) {
  const [query, setQuery] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(query);
  };

  return (
    <form onSubmit={handleSubmit} className="w-[85%] bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-3.5 shadow-sm space-y-2">
      <p className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 font-sans">Shipment Tracking</p>
      <input
        type="text"
        required
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Email or Transaction Ref ID"
        className="w-full text-xs p-2 border border-zinc-200 dark:border-zinc-800 rounded-lg outline-none focus:border-amber-500 bg-zinc-50 dark:bg-zinc-950 dark:text-white"
      />
      <button
        type="submit"
        disabled={loading}
        className="w-full py-2 bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-700 hover:to-yellow-700 text-white font-bold rounded-lg text-xs flex items-center justify-center gap-1"
      >
        {loading ? <FaSpinner className="animate-spin" /> : 'Track Order'}
      </button>
    </form>
  );
}

// Cart items preview helper
function CartItemsPreview({ data, onClose }) {
  const { items, cartTotal, totalItems } = data;

  const handleCheckoutClick = () => {
    onClose();
    window.location.hash = '#pay'; // hash anchor to pay form
    window.location.pathname = '/cart'; // push route path
  };

  return (
    <div className="w-[90%] bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-3 shadow-md space-y-3 font-sans">
      <div className="flex justify-between items-center pb-2 border-b border-zinc-100 dark:border-zinc-800">
        <span className="text-xs font-bold text-zinc-700 dark:text-zinc-200">Your Cart Items ({totalItems})</span>
        <span className="text-xs font-semibold text-amber-600">{formatPrice(cartTotal)}</span>
      </div>

      <div className="max-h-[140px] overflow-y-auto space-y-2 pr-1 scrollbar-thin">
        {items.map((item) => (
          <div key={item.id} className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <img
                src={item.product_media?.[0] || item.product_image?.[0] || item.product_image || "https://via.placeholder.com/50"}
                alt={item.product_name || item.title || "Product"}
                className="w-8 h-8 rounded border border-zinc-200 dark:border-zinc-800 object-cover"
                onError={(e) => {
                  e.target.src = "https://via.placeholder.com/50";
                }}
              />
              <span className="font-medium text-zinc-800 dark:text-zinc-200 line-clamp-1 max-w-[130px]">
                {item.product_name || item.title || "Unknown Product"}
              </span>
            </div>
            <span className="text-zinc-500">Qty: {item.quantity}</span>
          </div>
        ))}
      </div>

      <button
        onClick={handleCheckoutClick}
        className="w-full py-2 bg-gradient-to-r from-amber-600 to-yellow-600 text-white font-bold rounded-lg text-xs flex items-center justify-center gap-1 shadow-md hover:scale-102 transition"
      >
        Complete Order (15% Off Code: CART15) <MdChevronRight size={16} />
      </button>
    </div>
  );
}

// Tracking results preview
function TrackingResults({ orders }) {
  return (
    <div className="w-[90%] space-y-3">
      {orders.map((order) => {
        const orderDate = order.createdAt?.seconds 
          ? new Date(order.createdAt.seconds * 1000).toLocaleDateString()
          : new Date(order.createdAt).toLocaleDateString();

        // Calculate delivery status stage index: 0 = Ordered, 1 = Processing, 2 = Shipped, 3 = Delivered
        let stage = 0; // default Ordered
        const status = (order.orderStatus || 'pending').toLowerCase();
        if (status === 'processing') stage = 1;
        else if (status === 'shipped') stage = 2;
        else if (status === 'delivered') stage = 3;

        return (
          <div key={order.id} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-3 shadow-md space-y-3 font-sans text-xs">
            <div className="flex justify-between items-center border-b border-zinc-100 dark:border-zinc-800 pb-1.5">
              <div>
                <p className="font-bold text-zinc-800 dark:text-zinc-200">Ref: {order.transactionId?.substring(0, 10) || order.id.substring(0, 10)}...</p>
                <p className="text-[10px] text-zinc-400">{orderDate}</p>
              </div>
              <span className="font-semibold text-amber-600">{formatPrice(order.amount)}</span>
            </div>

            {/* Products */}
            <p className="text-[11px] text-zinc-600 dark:text-zinc-300 font-medium line-clamp-2">
              <span className="font-semibold text-zinc-800 dark:text-zinc-100">Items: </span>
              {order.productPaidFor || 'Premium Goods'}
            </p>

            {/* Status Progress Bar */}
            <div className="pt-2">
              <div className="flex items-center justify-between text-[10px] font-semibold text-zinc-400 dark:text-zinc-500 mb-1 px-1">
                <span className={stage >= 0 ? 'text-emerald-500' : ''}>Ordered</span>
                <span className={stage >= 1 ? 'text-emerald-500' : ''}>Processing</span>
                <span className={stage >= 2 ? 'text-emerald-500' : ''}>Shipped</span>
                <span className={stage >= 3 ? 'text-emerald-500' : ''}>Delivered</span>
              </div>
              
              <div className="w-full bg-zinc-200 dark:bg-zinc-800 h-1.5 rounded-full relative overflow-hidden">
                <div 
                  className="bg-emerald-500 h-full rounded-full transition-all duration-500"
                  style={{ width: `${(stage / 3) * 100}%` }}
                />
              </div>

              {stage === 0 && (
                <p className="mt-2 text-[10px] text-amber-600 font-medium">Your order has been received and is awaiting processing.</p>
              )}
              {stage === 1 && (
                <p className="mt-2 text-[10px] text-amber-600 font-medium">Our design & warehouse team is assembling your premium order.</p>
              )}
              {stage === 2 && (
                <p className="mt-2 text-[10px] text-emerald-600 font-medium">Out for delivery! Your shipment is on its way.</p>
              )}
              {stage === 3 && (
                <p className="mt-2 text-[10px] text-emerald-600 font-medium">Delivered. Thank you for shopping with Veth Interiors!</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// FAQ Accordion list
function FAQList() {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      q: 'How long does shipping take?',
      a: 'Shipping typically takes 3-5 business days depending on your location. Premium bespoke furniture may take 2-4 weeks to manufacture and ship.'
    },
    {
      q: 'What is your return policy?',
      a: 'We offer a 7-day return policy for standard, unused items. Custom and bespoke items are final sale and non-refundable.'
    },
    {
      q: 'Do you offer international shipping?',
      a: 'Yes, we ship globally! Shipping rates and duties are computed automatically at checkout based on your country.'
    },
    {
      q: 'How can I contact Veth Support?',
      a: 'You can email us at support@vethinteriors.com or click the WhatsApp float bubble to chat with our operations team directly.'
    }
  ];

  return (
    <div className="w-[90%] bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl p-2 shadow-md space-y-1">
      {faqs.map((faq, i) => (
        <div key={i} className="border-b border-zinc-100 dark:border-zinc-800/80 last:border-0 pb-1 last:pb-0">
          <button
            onClick={() => setOpenIndex(openIndex === i ? null : i)}
            className="w-full text-left py-2 px-2 text-xs font-semibold text-zinc-700 dark:text-zinc-200 flex justify-between items-center hover:bg-amber-50/30 rounded"
          >
            <span>{faq.q}</span>
            <span className="text-zinc-400 font-bold">{openIndex === i ? '−' : '+'}</span>
          </button>
          {openIndex === i && (
            <p className="px-2 pb-2 text-[11px] text-zinc-500 dark:text-zinc-400 leading-relaxed font-sans font-medium whitespace-pre-line">
              {faq.a}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
